import React, { useMemo } from "react";
import PropTypes from "prop-types";
import { Box, CircularProgress } from "@mui/material";
import CustomizedTable from "../../CustomizedTable/CustomizedTable";
import { DefaultCell, TitleCaseCell, UpCaseCell } from "../../CustomizedTable/CustomCells/CustomCells";
import CarrierNameCell from "../../CustomizedTable/CustomCells/CarrierNameCell/CarrierNameCell";
import BrokerageStatusCell from "../../CustomizedTable/CustomCells/BrokerageStatusCell/BrokerageStatusCell";
import PipelineStatusCell from "../../CustomizedTable/CustomCells/PipelineStatusCell/PipelineStatusCell";
import CurrencyCell from "../../CustomizedTable/CustomCells/CurrencyCell/CurrencyCell";
import DateTimeRangeCell from "../../CustomizedTable/CustomCells/DateTimeRangeCell/DateTimeRangeCell";
import BrokerageStatusFilter from "../../CustomizedTable/Filters/BrokerageStatusFilter/BrokerageStatusFilter";
import MultiOptionsFilter from "../../CustomizedTable/Filters/MultipleOptionsFilter/MultipleOptionsFilter";
import DateRangeFilter from "../../CustomizedTable/Filters/DateRangeFilter/DateRangeFilter";
import NumberRangeFilter from "../../CustomizedTable/Filters/NumberRangeFilter/NumberRangeFilter";
import SelectColumnFilter from "../../CustomizedTable/Filters/SelectColumnFilter/SelectColumnFilter";
import LoadIdCell from "../../CustomizedTable/CustomCells/OrderIdCell/LoadIdCell";
import ModeWithTempCell from "../../CustomizedTable/CustomCells/ModeWithTempCell/ModeWithTempCell";

export default function CarrierSearchOrderLevel({
  isLoading,
  search,
  orderLevelResults,
  handleTableAction
}) {
  const results = useMemo(() => orderLevelResults, [orderLevelResults]);

  const columns = useMemo(
    () => [
      {
        Header: "Carrier",
        accessor: "carrierName",
        filter: "fuzzyText",
        width: 240,
        idKey: "carrierId",
        originPoint: "carrier-search",
        showCanBook: true,
        Cell: CarrierNameCell,
      },
      {
        Header: "Carrier Status",
        accessor: "active",
        Filter: SelectColumnFilter,
        filter: "equal",
        width: 140,
        Cell: DefaultCell
      },
      {
        Header: "Carrier Rep",
        accessor: "dispatcherUserId",
        filter: "fuzzyText",
        width: 130,
        Cell: DefaultCell,
      },
      {
        Header: "Carrier Mgr",
        accessor: "accountManagerId",
        filter: "fuzzyText",
        width: 120,
        Cell: DefaultCell
      },
      {
        Header: "Pipeline Status",
        accessor: "pipelineStatus",
        Filter: SelectColumnFilter,
        filter: "equal",
        width: 160,
        Cell: PipelineStatusCell,
      },
      {
        Header: "LH + Fuel",
        accessor: "carrierPay",
        Filter: NumberRangeFilter,
        filter: "between",
        width: 130,
        Cell: CurrencyCell,
      },
      {
        Header: "Customer Charge",
        accessor: "orderTotalCharge",
        Filter: NumberRangeFilter,
        filter: "between",
        width: 160,
        Cell: CurrencyCell,
      },
      {
        Header: "Load",
        accessor: "orderId",
        idKey: "orderId",
        filter: "fuzzyText",
        width: 90,
        Cell: LoadIdCell,
      },
      {
        Header: "Brokerage Status",
        accessor: "brokerageStatus",
        Filter: BrokerageStatusFilter,
        filter: "multi",
        width: 160,
        Cell: BrokerageStatusCell,
      },
      {
        Header: "Mode",
        accessor: "mode",
        Filter: SelectColumnFilter,
        filter: "equal",
        width: 160,
        Cell: ModeWithTempCell,
      },
      {
        Header: "Origin City",
        accessor: "originCityName",
        filter: "fuzzyText",
        width: 130,
        Cell: UpCaseCell,
      },
      {
        Header: "OS",
        accessor: "originState",
        Filter: MultiOptionsFilter,
        filter: "multi",
        width: 80,
        Cell: DefaultCell,
      },
      {
        Header: "Pick Up",
        accessor: "pickupDate",
        Filter: DateRangeFilter,
        filter: "dateBetween",
        width: 140,
        Cell: DateTimeRangeCell,
      },
      {
        Header: "Dest City",
        accessor: "destinationCityName",
        filter: "fuzzyText",
        width: 130,
        Cell: UpCaseCell,
      },
      {
        Header: "DS",
        accessor: "destinationState",
        Filter: MultiOptionsFilter,
        filter: "multi",
        width: 80,
        Cell: DefaultCell,
      },
      {
        Header: "Delivery",
        accessor: "deliveryDate",
        Filter: DateRangeFilter,
        filter: "dateBetween",
        width: 140,
        Cell: DateTimeRangeCell,
      },
      {
        Header: "Customer",
        accessor: "customerName",
        filter: "fuzzyText",
        width: 320,
        Cell: DefaultCell,
      },
      {
        Header: "Commodity",
        accessor: "commodity",
        filter: "fuzzyText",
        width: 130,
        Cell: TitleCaseCell,
      },
    ],
    []
  );

  return (
    <Box>
      {isLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", marginTop: "20px" }}>
          <CircularProgress sx={{ color: "primary.60" }} size={40} />
        </Box>
      ) : (
        <CustomizedTable
          id="order-level-search"
          columns={columns}
          data={results}
          tableSearch={search}
          actionCallback={handleTableAction}
        />
      )}
    </Box>
  );
}

CarrierSearchOrderLevel.propTypes = {
  isLoading: PropTypes.bool,
  search: PropTypes.string,
  orderLevelResults: PropTypes.array.isRequired,
  handleTableAction: PropTypes.func
};
